import type { Namespace, TFunction } from 'i18next';
import {
  faAt,
  faFileLines,
  faFilePdf,
  faLink,
  faPhone,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const categoryActionTypes = [
  'url',
  'phoneNumber',
  'emailAddress',
  'article',
  'pdf',
] as const;
export type ICategoryActionType = (typeof categoryActionTypes)[number];

export type IAction = {
  type: ICategoryActionType;
  label: string;
  supportingText?: string;
  icon?: React.ReactNode;
  prefix?: string;
  placeholder: string;
  textInputFieldType: string;
  autoComplete: string;
};

export const getCategoryActionFromType = <TNamespace extends Namespace>(
  type: string,
  t: TFunction<TNamespace>,
): IAction | undefined => {
  switch (type) {
    case 'url':
      return {
        type,
        label: t('CategoryAction.url'),
        supportingText: t(
          'CategoryAction.opens-the-link-in-the-default-browser',
        ),
        prefix: undefined,
        placeholder: 'Example: https://example.com',
        textInputFieldType: 'url',
        autoComplete: 'url',
        icon: <FontAwesomeIcon icon={faLink} />,
      };

    case 'phoneNumber':
      return {
        type,
        label: t('CategoryAction.phone-number'),
        supportingText: t(
          'CategoryAction.asks-the-user-for-confirmation-to-call-a-phone-number',
        ),
        prefix: 'tel:',
        placeholder: 'Example: +33939111234',
        textInputFieldType: 'tel',
        autoComplete: 'tel',
        icon: <FontAwesomeIcon icon={faPhone} />,
      };

    case 'emailAddress':
      return {
        type,
        label: t('CategoryAction.email-address'),
        supportingText: t(
          'CategoryAction.opens-the-default-email-application-and-compose-a-new-message',
        ),
        prefix: 'mailto:',
        placeholder: 'Example: contact@crowdaa.com',
        textInputFieldType: 'email',
        autoComplete: 'email',
        icon: <FontAwesomeIcon icon={faAt} />,
      };

    case 'article':
      return {
        type,
        label: t('CategoryAction.article'),
        supportingText: t(
          'CategoryAction.opens-a-specific-article-in-the-application',
        ),
        prefix: '/articles/',
        placeholder:
          'ID of the article. Example: b935ad53-1bc7-4028-9a26-14b4646edfa4',
        textInputFieldType: 'text',
        autoComplete: 'off',
        icon: <FontAwesomeIcon icon={faFileLines} />,
      };

    case 'pdf':
      return {
        type,
        label: t('CategoryAction.pdf'),
        supportingText: t('CategoryAction.opens-a-pdf-in-the-default-viewer'),
        prefix: '/pdf/',
        placeholder: 'Example: https://example.com/file.pdf',
        textInputFieldType: 'url',
        autoComplete: 'url',
        icon: <FontAwesomeIcon icon={faFilePdf} />,
      };
  }

  return undefined;
};
